/**
 * Reading Activities Service
 *
 * Manages reading activities - the source of truth for user's reading journey.
 * Each activity represents a discrete event (started, finished, DNF, etc.)
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../config";
import {
  ActivityType,
  isValidActivityType,
  isValidActivityTransition,
  getCurrentStatusFromActivity,
  GoalCountingActivityTypes,
} from "../constants/activityTypes";

const ACTIVITIES_COLLECTION = "ReadingActivities";

/**
 * Create a new reading activity
 *
 * @param {Object} activityData
 * @param {string} activityData.userId - User ID
 * @param {string} activityData.bookId - Book ID
 * @param {string} activityData.activityType - Type from ActivityType enum
 * @param {string} activityData.date - ISO date when activity occurred (user-editable)
 * @param {Object} activityData.metadata - Activity-specific data
 * @param {string} activityData.visibility - "public" or "private"
 * @returns {Promise<string>} Created activity ID
 */
export async function createActivity({
  userId,
  bookId,
  activityType,
  date = new Date().toISOString(),
  metadata = {},
  visibility = "public",
}) {
  // Validation
  if (!userId || !bookId) {
    throw new Error("userId and bookId are required");
  }

  if (!isValidActivityType(activityType)) {
    throw new Error(`Invalid activity type: ${activityType}`);
  }

  // Check if transition is valid
  const latestActivity = await getLatestActivity(userId, bookId);
  if (
    latestActivity &&
    !isValidActivityTransition(latestActivity.activityType, activityType)
  ) {
    throw new Error(
      `Invalid transition from ${latestActivity.activityType} to ${activityType}`
    );
  }

  // Create activity document
  const activityData = {
    userId,
    bookId,
    activityType,
    date: date, // When the activity happened (user can backdate)
    createdAt: new Date().toISOString(), // When it was logged
    updatedAt: new Date().toISOString(),
    metadata: {
      ...metadata,
      // Auto-calculate reread if this is a second FINISHED activity
      ...(activityType === ActivityType.FINISHED && {
        readThroughNumber:
          metadata.readThroughNumber ||
          (await calculateReadThroughNumber(userId, bookId)),
        isReread:
          metadata.isReread !== undefined
            ? metadata.isReread
            : (await getFinishedCount(userId, bookId)) > 0,
      }),
    },
    visibility,
    socialMetadata: {
      likes: 0,
      comments: 0,
    },
  };

  const docRef = await addDoc(
    collection(db, ACTIVITIES_COLLECTION),
    activityData
  );

  return docRef.id;
}

/**
 * Get latest activity for a user-book combination
 */
export async function getLatestActivity(userId, bookId) {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where("userId", "==", userId),
    where("bookId", "==", bookId),
    orderBy("date", "desc"),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
}

/**
 * Get all activities for a user-book combination (timeline)
 */
export async function getBookActivities(userId, bookId, options = {}) {
  const { limitCount = null, lastDoc = null } = options;

  let q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where("userId", "==", userId),
    where("bookId", "==", bookId),
    orderBy("date", "desc")
  );

  if (limitCount) {
    q = query(q, limit(limitCount));
  }

  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const snapshot = await getDocs(q);

  // If no pagination options provided, return old format for backward compatibility
  if (!limitCount && !lastDoc) {
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  }

  // Return paginated format
  return {
    activities: snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })),
    lastDoc: snapshot.docs[snapshot.docs.length - 1],
    hasMore: snapshot.docs.length === limitCount,
  };
}

/**
 * Get current reading status from activities
 */
export async function getCurrentStatus(userId, bookId) {
  const latestActivity = await getLatestActivity(userId, bookId);

  if (!latestActivity) {
    return null;
  }

  return getCurrentStatusFromActivity(latestActivity.activityType);
}

/**
 * Get user's activity feed (all books)
 */
export async function getUserActivityFeed(userId, limitCount = 50) {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where("userId", "==", userId),
    where("visibility", "==", "public"),
    orderBy("date", "desc"),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Calculate which read-through number this is
 */
async function calculateReadThroughNumber(userId, bookId) {
  const finishedCount = await getFinishedCount(userId, bookId);
  return finishedCount + 1; // Next read-through
}

/**
 * Get count of FINISHED activities for this book
 */
async function getFinishedCount(userId, bookId) {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where("userId", "==", userId),
    where("bookId", "==", bookId),
    where("activityType", "==", ActivityType.FINISHED)
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Update an existing activity (for editing date, metadata, etc.)
 */
export async function updateActivity(activityId, updates) {
  const activityRef = doc(db, ACTIVITIES_COLLECTION, activityId);

  const updatedData = {
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  await updateDoc(activityRef, updatedData);

  return activityId;
}

/**
 * Delete an activity
 */
export async function deleteActivity(activityId) {
  const activityRef = doc(db, ACTIVITIES_COLLECTION, activityId);
  await deleteDoc(activityRef);
}

/**
 * Get activities that count toward reading goals for a time period
 */
export async function getGoalActivities(userId, startDate, endDate) {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where("userId", "==", userId),
    where("activityType", "in", GoalCountingActivityTypes),
    where("date", ">=", startDate),
    where("date", "<=", endDate),
    orderBy("date", "desc")
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Get count of books finished in a date range
 */
export async function getBooksFinishedCount(userId, startDate, endDate) {
  const activities = await getGoalActivities(userId, startDate, endDate);

  // Count unique books (in case of rereads)
  const uniqueBooks = new Set(activities.map((a) => a.bookId));

  return uniqueBooks.size;
}

/**
 * Get reading stats from activities
 */
export async function getReadingStatsFromActivities(userId, year) {
  const startDate = `${year}-01-01T00:00:00.000Z`;
  const endDate = `${year}-12-31T23:59:59.999Z`;

  const activities = await getGoalActivities(userId, startDate, endDate);

  const stats = {
    totalBooksFinished: 0,
    totalRereads: 0,
    booksFinishedByMonth: {},
    dnfCount: 0,
  };

  const finishedBooks = new Set();

  for (const activity of activities) {
    if (activity.activityType === ActivityType.FINISHED) {
      finishedBooks.add(activity.bookId);

      if (activity.metadata?.isReread) {
        stats.totalRereads++;
      }

      // Count by month
      const month = activity.date.substring(0, 7); // "2024-03"
      stats.booksFinishedByMonth[month] =
        (stats.booksFinishedByMonth[month] || 0) + 1;
    }

    if (activity.activityType === ActivityType.DNF) {
      stats.dnfCount++;
    }
  }

  stats.totalBooksFinished = finishedBooks.size;

  return stats;
}

/**
 * Check if user is currently reading a book
 */
export async function isCurrentlyReading(userId, bookId) {
  const latestActivity = await getLatestActivity(userId, bookId);

  if (!latestActivity) return false;

  const activeTypes = [
    ActivityType.STARTED,
    ActivityType.RESUMED,
    ActivityType.PROGRESS_UPDATE,
  ];

  return activeTypes.includes(latestActivity.activityType);
}

/**
 * Batch create activities (for migration)
 */
export async function batchCreateActivities(activities) {
  const results = [];

  // Process in batches of 500 to avoid Firestore limits
  const batchSize = 500;

  for (let i = 0; i < activities.length; i += batchSize) {
    const batch = activities.slice(i, i + batchSize);

    const promises = batch.map((activity) => createActivity(activity));

    const batchResults = await Promise.all(promises);
    results.push(...batchResults);
  }

  return results;
}

/**
 * Helper: Get STARTED activity for a read-through
 */
export async function getStartedActivityForReadThrough(
  userId,
  bookId,
  readThroughNumber
) {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where("userId", "==", userId),
    where("bookId", "==", bookId),
    where("activityType", "==", ActivityType.STARTED),
    orderBy("date", "asc")
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty || snapshot.size < readThroughNumber) {
    return null;
  }

  const activity = snapshot.docs[readThroughNumber - 1];
  return { id: activity.id, ...activity.data() };
}